import React, { useEffect, useRef } from "react";
import {
  mailerLiteFormHtml,
  MAILERLITE_FORM_ID,
} from "../data/mailerLiteForm";
import { META_PIXEL_ID } from "../data/metaPixel";

// ---------------------------------------------------------------------------
// MailerLiteForm — shared wrapper around the MailerLite embedded signup
// form (HTML code variant). Extracted from the Newsletter page so any page
// on the site can drop in the same working embed without re-wiring the
// runtime pieces MailerLite needs:
//   1. A global success callback (named `ml_webform_success_<formId>`)
//      that the form's own JS calls after a successful subscription.
//   2. Their webforms.min.js library, loaded via <script> tag, which
//      intercepts the form submit and POSTs over fetch instead of
//      opening the form's `target="_blank"` action in a new tab.
//   3. A one-shot analytics fetch (`/takel`) that registers the form
//      load with MailerLite for their dashboard stats.
//
// The form's own copy (heading / intro paragraph / submit button label)
// is baked into the mailerLiteFormHtml string so it stays identical across
// every page by default. Pass `heading`, `description`, and/or `ctaLabel`
// to override just the visible text for a specific placement (e.g. a
// campaign landing page) without forking the shared HTML/CSS or affecting
// other pages that use the same embed.
// ---------------------------------------------------------------------------
const WEBFORMS_SCRIPT_SRC =
  "https://groot.mailerlite.com/js/w/webforms.min.js?v176e10baa5e7ed80d35ae235be3d5024";
const TAKEL_URL =
  "https://assets.mailerlite.com/jsonp/1692674/forms/160969281612285195/takel";

function MailerLiteForm({ className, heading, description, ctaLabel }) {
  const containerRef = useRef(null);

  useEffect(() => {
    // 1. Success callback — name must match what the form's inline
    //    script (provided by MailerLite) will call on success.
    window[`ml_webform_success_${MAILERLITE_FORM_ID}`] = function () {
      // Fire the Meta pixel conversion on a genuine, successful subscription.
      // MailerLite invokes this callback only after the signup succeeds, so it
      // is the correct trigger — unlike Meta's auto-detected button event,
      // which falsely fires on every page that shows the "Mailing List" link.
      if (window.fbq) {
        // MailerLite's webforms.min.js hides/shows rows via jQuery rather
        // than unmounting the form, so the email input is still readable
        // here — grab it for Advanced Matching before tracking the event.
        const emailInput = document.querySelector(
          `#mlb2-${MAILERLITE_FORM_ID} input[name="fields[email]"]`
        );
        const email = emailInput && emailInput.value;
        if (email) {
          window.fbq("init", META_PIXEL_ID, { em: email.trim().toLowerCase() });
        }
        window.fbq("trackCustom", "NewsletterSignup");
      }

      const $ = window.ml_jQuery || window.jQuery;
      if (!$) return;
      $(`.ml-subscribe-form-${MAILERLITE_FORM_ID} .row-success`).show();
      $(`.ml-subscribe-form-${MAILERLITE_FORM_ID} .row-form`).hide();
    };

    // 2. Inject the webforms script if it isn't already on the page.
    //    Once loaded the script auto-binds to any .ml-subscribe-form
    //    elements in the DOM.
    if (!document.querySelector(`script[src="${WEBFORMS_SCRIPT_SRC}"]`)) {
      const injectedScript = document.createElement("script");
      injectedScript.src = WEBFORMS_SCRIPT_SRC;
      injectedScript.async = true;
      document.body.appendChild(injectedScript);
    }

    // 3. Register the form load with MailerLite analytics. Failure is
    //    non-critical; ignore network errors.
    fetch(TAKEL_URL).catch(() => {});

    // We deliberately don't remove the script on unmount — leaving it
    // cached makes subsequent visits render instantly.
  }, []);

  // Optional per-placement text overrides. Runs after the embed HTML is
  // in the DOM (dangerouslySetInnerHTML renders synchronously with this
  // component, before effects run) and only touches text nodes — the
  // MailerLite markup, classes, and submit behaviour are untouched.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    if (heading) {
      const h4 = root.querySelector(".ml-form-embedContent h4");
      if (h4) h4.textContent = heading;
    }
    if (description) {
      const p = root.querySelector(".ml-form-embedContent p");
      if (p) p.textContent = description;
    }
    if (ctaLabel) {
      const btn = root.querySelector(".ml-form-embedSubmit button.primary");
      if (btn) btn.textContent = ctaLabel;
    }
  }, [heading, description, ctaLabel]);

  return (
    <div
      ref={containerRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: mailerLiteFormHtml }}
    />
  );
}

export default MailerLiteForm;
